/**
 * Sankey diagrams from lines of `Source -> Target: value`.
 *
 * A Sankey diagram draws flows as ribbons whose widths are proportional to the amount
 * carried, between nodes stacked in columns. Everything here is deterministic.
 *
 * - **Reading.** `A -> B: 120`, `A → B: 120` and the SankeyMATIC form `A [120] B` all
 *   work; `#` and `//` start a comment. Repeat lines add up. A link that would close a
 *   loop is left out (a Sankey flows one way), as are self-links and zero or negative
 *   values.
 * - **Columns.** A node's column is its longest path from a source, with pure sinks moved
 *   to the last column so every flow ends at the right-hand edge.
 * - **Order.** Within a column nodes start in the order they first appear, and — unless the
 *   reader asks for input order — barycentre sweeps reorder them to cut crossings.
 * - **Height.** One scale for the whole diagram, chosen so the fullest column fits, so a
 *   ribbon of 10 is exactly twice as wide as a ribbon of 5 wherever it is.
 * - **Position.** Columns are centred, then relaxed toward their neighbours' centres with
 *   each column pushed apart to keep a gap, so no two boxes ever overlap.
 */

import { parseNumberInput } from "@/lib/parse-number";
import type { Rect } from "@/lib/tool-kit/charts/layout";
import { LINE_HEIGHT } from "@/lib/tool-kit/charts/layout";
import {
  maxTextWidth,
  type TextStyle,
  wrapText,
} from "@/lib/tool-kit/charts/text";

/* ------------------------------------------------------------------- parse */

export interface SankeyNodeInfo {
  id: number;
  name: string;
  /** First line the node appeared on. */
  line: number;
}

export interface SankeyLinkInfo {
  source: number;
  target: number;
  value: number;
  line: number;
}

export type SankeyWarningKind =
  | "badLine"
  | "badValue"
  | "selfLink"
  | "cycle"
  | "tooMany";

export interface SankeyWarning {
  kind: SankeyWarningKind;
  line: number;
  text: string;
}

export interface SankeyParse {
  nodes: SankeyNodeInfo[];
  links: SankeyLinkInfo[];
  warnings: SankeyWarning[];
}

export const SANKEY_NODE_LIMIT = 40;
export const SANKEY_LINK_LIMIT = 100;

const LINE_BREAK = /\r\n|\r|\n/;
const COMMENT = /^\s*(?:#|\/\/)/;
const ARROWED = /^(.+?)\s*(?:->|→|=>|>)\s*(.+)$/;
const BRACKETED = /^(.+?)\s*\[\s*([^\]]+?)\s*\]\s*(.+)$/;
const WHITESPACE = /\s+/g;

function key(name: string): string {
  return name.replace(WHITESPACE, " ").trim().toLowerCase();
}

interface RawLink {
  source: string;
  target: string;
  value: number | null;
}

/** Split `A -> B: 12` or `A [12] B` into its three parts, or null. */
function splitLine(line: string): RawLink | null {
  const bracket = BRACKETED.exec(line);
  if (bracket) {
    return {
      source: bracket[1].trim(),
      target: bracket[3].trim(),
      value: parseNumberInput(bracket[2]),
    };
  }
  const arrow = ARROWED.exec(line);
  if (!arrow) {
    return null;
  }
  const rest = arrow[2];
  const colon = rest.lastIndexOf(":");
  if (colon < 0) {
    return null;
  }
  return {
    source: arrow[1].trim(),
    target: rest.slice(0, colon).trim(),
    value: parseNumberInput(rest.slice(colon + 1)),
  };
}

/** Links whose removal leaves no loop: depth-first, dropping each edge that closes one. */
function loopClosers(
  count: number,
  links: readonly SankeyLinkInfo[]
): Set<number> {
  const out: number[][] = Array.from({ length: count }, () => []);
  for (const [i, link] of links.entries()) {
    out[link.source].push(i);
  }
  const state = new Array<number>(count).fill(0);
  const closers = new Set<number>();
  const visit = (node: number) => {
    state[node] = 1;
    for (const i of out[node]) {
      const next = links[i].target;
      if (state[next] === 1) {
        closers.add(i);
      } else if (state[next] === 0) {
        visit(next);
      }
    }
    state[node] = 2;
  };
  for (let node = 0; node < count; node++) {
    if (state[node] === 0) {
      visit(node);
    }
  }
  return closers;
}

interface Draft {
  nodes: SankeyNodeInfo[];
  byKey: Map<string, number>;
  links: SankeyLinkInfo[];
  warnings: SankeyWarning[];
}

function nodeIndex(draft: Draft, name: string, line: number): number | null {
  const known = draft.byKey.get(key(name));
  if (known !== undefined) {
    return known;
  }
  if (draft.nodes.length >= SANKEY_NODE_LIMIT) {
    draft.warnings.push({ kind: "tooMany", line, text: name });
    return null;
  }
  draft.nodes.push({ id: draft.nodes.length, name, line });
  draft.byKey.set(key(name), draft.nodes.length - 1);
  return draft.nodes.length - 1;
}

function addFlow(draft: Draft, raw: RawLink, line: number): void {
  if (raw.source === "" || raw.target === "") {
    draft.warnings.push({
      kind: "badLine",
      line,
      text: `${raw.source} ${raw.target}`.trim(),
    });
    return;
  }
  if (raw.value === null || !(raw.value > 0) || !Number.isFinite(raw.value)) {
    draft.warnings.push({
      kind: "badValue",
      line,
      text: `${raw.source} → ${raw.target}`,
    });
    return;
  }
  if (key(raw.source) === key(raw.target)) {
    draft.warnings.push({ kind: "selfLink", line, text: raw.source });
    return;
  }
  const source = nodeIndex(draft, raw.source, line);
  const target = nodeIndex(draft, raw.target, line);
  if (source === null || target === null) {
    return;
  }
  const same = draft.links.find(
    (link) => link.source === source && link.target === target
  );
  if (same) {
    same.value += raw.value;
    return;
  }
  if (draft.links.length >= SANKEY_LINK_LIMIT) {
    draft.warnings.push({
      kind: "tooMany",
      line,
      text: `${raw.source} → ${raw.target}`,
    });
    return;
  }
  draft.links.push({ source, target, value: raw.value, line });
}

/** Read the flows. Anything that cannot be drawn is left out with a warning. */
export function parseSankey(text: string): SankeyParse {
  const draft: Draft = { nodes: [], byKey: new Map(), links: [], warnings: [] };
  for (const [index, raw] of text.split(LINE_BREAK).entries()) {
    if (raw.trim() === "" || COMMENT.test(raw)) {
      continue;
    }
    const split = splitLine(raw.trim());
    if (split === null) {
      draft.warnings.push({
        kind: "badLine",
        line: index + 1,
        text: raw.trim(),
      });
      continue;
    }
    addFlow(draft, split, index + 1);
  }
  const closers = loopClosers(draft.nodes.length, draft.links);
  const links = draft.links.filter((_, i) => !closers.has(i));
  for (const i of closers) {
    const link = draft.links[i];
    draft.warnings.push({
      kind: "cycle",
      line: link.line,
      text: `${draft.nodes[link.source].name} → ${draft.nodes[link.target].name}`,
    });
  }
  // Nodes left with no link (every flow of theirs was dropped) are not drawn.
  const used = new Set(links.flatMap((link) => [link.source, link.target]));
  const keep = draft.nodes.filter((node) => used.has(node.id));
  const remap = new Map(keep.map((node, i) => [node.id, i]));
  return {
    nodes: keep.map((node, i) => ({ ...node, id: i })),
    links: links.map((link) => ({
      ...link,
      source: remap.get(link.source) as number,
      target: remap.get(link.target) as number,
    })),
    warnings: draft.warnings.sort((a, b) => a.line - b.line),
  };
}

/* ------------------------------------------------------------------ layout */

export type SankeyOrder = "crossings" | "input";

export const SANKEY_FONT = 12;
const LABEL_STYLE: TextStyle = { fontSize: SANKEY_FONT };
const LABEL_LINE = SANKEY_FONT * LINE_HEIGHT;
const NODE_WIDTH = 16;
const NODE_PAD = 30;
const MARGIN = 16;
const LABEL_GAP = 6;
const MIN_NODE_HEIGHT = 2;
const MIN_PLOT = 300;
const PER_NODE = 40;
const COLUMN_GAP_MIN = 170;
const COLUMN_GAP_MAX = 260;
const TARGET_WIDTH = 760;
const RELAX_PASSES = 8;
const SWEEPS = 6;

export interface PlacedSankeyNode {
  node: SankeyNodeInfo;
  column: number;
  /** Larger of the flow in and the flow out. */
  value: number;
  inflow: number;
  outflow: number;
  rect: Rect;
  /** Label lines and where they sit. */
  label: { lines: string[]; rect: Rect; anchor: "start" | "end" };
}

export interface PlacedSankeyLink {
  link: SankeyLinkInfo;
  /** Ribbon thickness: `value × scale`. */
  width: number;
  /** Centre line start (source edge) and end (target edge). */
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface SankeyLayout {
  width: number;
  height: number;
  /** Height in drawing units of one unit of flow. */
  scale: number;
  columns: number;
  nodes: PlacedSankeyNode[];
  links: PlacedSankeyLink[];
}

function columnsOf(parse: SankeyParse): number[] {
  const count = parse.nodes.length;
  const column = new Array<number>(count).fill(0);
  // Longest path from a source: relax in passes (the graph is acyclic).
  for (let pass = 0; pass < count; pass++) {
    let changed = false;
    for (const link of parse.links) {
      if (column[link.target] < column[link.source] + 1) {
        column[link.target] = column[link.source] + 1;
        changed = true;
      }
    }
    if (!changed) {
      break;
    }
  }
  const last = Math.max(0, ...column);
  const hasOut = new Set(parse.links.map((link) => link.source));
  return column.map((c, i) => (hasOut.has(i) ? c : last));
}

function flows(parse: SankeyParse): { inflow: number[]; outflow: number[] } {
  const inflow = new Array<number>(parse.nodes.length).fill(0);
  const outflow = new Array<number>(parse.nodes.length).fill(0);
  for (const link of parse.links) {
    outflow[link.source] += link.value;
    inflow[link.target] += link.value;
  }
  return { inflow, outflow };
}

/** The flow-weighted mean of `where` over a node's neighbours on one side, or null. */
function neighbourMean(
  parse: SankeyParse,
  id: number,
  towards: "source" | "target",
  where: (neighbour: number) => number
): number | null {
  let total = 0;
  let weight = 0;
  for (const link of parse.links) {
    const isNear =
      towards === "source" ? link.target === id : link.source === id;
    if (isNear) {
      total +=
        where(towards === "source" ? link.source : link.target) * link.value;
      weight += link.value;
    }
  }
  return weight > 0 ? total / weight : null;
}

/** Reorder each column by the mean position of its neighbours, keeping ties stable. */
function sweepOrder(parse: SankeyParse, columns: number[][]): void {
  const position = new Map<number, number>();
  const refresh = () => {
    for (const members of columns) {
      for (const [i, id] of members.entries()) {
        position.set(id, i);
      }
    }
  };
  refresh();
  const reorder = (members: number[], towards: "source" | "target") => {
    const score = (id: number): number =>
      neighbourMean(parse, id, towards, (other) => position.get(other) ?? 0) ??
      position.get(id) ??
      0;
    const before = new Map(members.map((id, i) => [id, i]));
    members.sort(
      (a, b) =>
        score(a) - score(b) || (before.get(a) ?? 0) - (before.get(b) ?? 0)
    );
  };
  for (let sweep = 0; sweep < SWEEPS; sweep++) {
    for (let c = 1; c < columns.length; c++) {
      reorder(columns[c], "source");
      refresh();
    }
    for (let c = columns.length - 2; c >= 0; c--) {
      reorder(columns[c], "target");
      refresh();
    }
  }
}

/** Push a column's boxes apart so each is `NODE_PAD` below the one above, inside the plot. */
function separate(
  members: readonly number[],
  y: number[],
  heights: readonly number[],
  top: number,
  bottom: number
): void {
  let edge = top;
  for (const id of members) {
    y[id] = Math.max(y[id], edge);
    edge = y[id] + heights[id] + NODE_PAD;
  }
  let limit = bottom;
  for (const id of [...members].reverse()) {
    y[id] = Math.min(y[id], limit - heights[id]);
    limit = y[id] - NODE_PAD;
  }
  // The upward pass can push the first box above `top` only if the column does not fit.
  edge = top;
  for (const id of members) {
    y[id] = Math.max(y[id], edge);
    edge = y[id] + heights[id] + NODE_PAD;
  }
}

function relax(
  parse: SankeyParse,
  columns: number[][],
  y: number[],
  heights: readonly number[],
  top: number,
  bottom: number
): void {
  const centre = (id: number) => y[id] + heights[id] / 2;
  const pull = (members: number[], towards: "source" | "target") => {
    for (const id of members) {
      const mean = neighbourMean(parse, id, towards, centre);
      if (mean !== null) {
        y[id] = mean - heights[id] / 2;
      }
    }
    separate(members, y, heights, top, bottom);
  };
  for (let pass = 0; pass < RELAX_PASSES; pass++) {
    for (let c = 1; c < columns.length; c++) {
      pull(columns[c], "source");
    }
    for (let c = columns.length - 2; c >= 0; c--) {
      pull(columns[c], "target");
    }
  }
}

function labelFor(
  node: SankeyNodeInfo,
  valueText: string,
  maxWidth: number
): string[] {
  const text = valueText === "" ? node.name : `${node.name} ${valueText}`;
  return wrapText(text, maxWidth, LABEL_STYLE, 2);
}

/**
 * Lay the diagram out. `valueText` formats a flow for node labels; pass `null` for
 * labels that show names only.
 */
export function layoutSankey(
  parse: SankeyParse,
  order: SankeyOrder,
  valueText: ((value: number) => string) | null
): SankeyLayout {
  const columnOf = columnsOf(parse);
  const count = Math.max(0, ...columnOf) + 1;
  const { inflow, outflow } = flows(parse);
  const value = parse.nodes.map((_, i) => Math.max(inflow[i], outflow[i]));
  const columns: number[][] = Array.from({ length: count }, () => []);
  for (const node of parse.nodes) {
    columns[columnOf[node.id]].push(node.id);
  }
  if (order === "crossings") {
    sweepOrder(parse, columns);
  }

  const gap = Math.min(
    COLUMN_GAP_MAX,
    Math.max(
      COLUMN_GAP_MIN,
      (TARGET_WIDTH - NODE_WIDTH) / Math.max(1, count - 1)
    )
  );
  const labelWidth = Math.max(80, Math.min(150, gap - NODE_WIDTH - 40));
  const labels = parse.nodes.map((node) =>
    labelFor(node, valueText ? valueText(value[node.id]) : "", labelWidth)
  );
  const outer = (column: number) =>
    Math.ceil(
      maxTextWidth(
        columns[column].flatMap((id) => labels[id]),
        LABEL_STYLE
      )
    ) + LABEL_GAP;
  const left = MARGIN + outer(0);
  const right = MARGIN + outer(count - 1);

  const tallest = Math.max(...columns.map((members) => members.length));
  const plotHeight = Math.max(MIN_PLOT, tallest * PER_NODE + 80);
  let scale = Number.POSITIVE_INFINITY;
  for (const members of columns) {
    const total = members.reduce((sum, id) => sum + value[id], 0);
    scale = Math.min(
      scale,
      (plotHeight - NODE_PAD * (members.length - 1)) / total
    );
  }
  const heights = value.map((v) => Math.max(MIN_NODE_HEIGHT, v * scale));

  const top = MARGIN;
  const bottom = MARGIN + plotHeight;
  const y = new Array<number>(parse.nodes.length).fill(0);
  for (const members of columns) {
    const stack =
      members.reduce((sum, id) => sum + heights[id], 0) +
      NODE_PAD * (members.length - 1);
    let at = top + (plotHeight - stack) / 2;
    for (const id of members) {
      y[id] = at;
      at += heights[id] + NODE_PAD;
    }
  }
  relax(parse, columns, y, heights, top, bottom);

  const x = (column: number) => left + column * gap;
  const nodes: PlacedSankeyNode[] = parse.nodes.map((node) => {
    const column = columnOf[node.id];
    const rect: Rect = {
      x: x(column),
      y: y[node.id],
      width: NODE_WIDTH,
      height: heights[node.id],
    };
    const lines = labels[node.id];
    const toLeft = column === 0;
    const textWidth = Math.ceil(maxTextWidth(lines, LABEL_STYLE));
    const textHeight = lines.length * LABEL_LINE;
    return {
      node,
      column,
      value: value[node.id],
      inflow: inflow[node.id],
      outflow: outflow[node.id],
      rect,
      label: {
        lines,
        anchor: toLeft ? "end" : "start",
        rect: {
          x: toLeft
            ? rect.x - LABEL_GAP - textWidth
            : rect.x + rect.width + LABEL_GAP,
          y: rect.y + rect.height / 2 - textHeight / 2,
          width: textWidth,
          height: textHeight,
        },
      },
    };
  });
  const links = placeLinks(parse, nodes, scale);
  const lowest = Math.max(
    bottom,
    ...nodes.map((n) => n.rect.y + n.rect.height),
    ...nodes.map((n) => n.label.rect.y + n.label.rect.height)
  );
  const wide = x(count - 1) + NODE_WIDTH + right;
  return {
    width: Math.ceil(wide),
    height: Math.ceil(lowest + MARGIN),
    scale,
    columns: count,
    nodes,
    links,
  };
}

/** Stack each node's ribbons in the order of the boxes at their other ends. */
function placeLinks(
  parse: SankeyParse,
  nodes: readonly PlacedSankeyNode[],
  scale: number
): PlacedSankeyLink[] {
  const placed: PlacedSankeyLink[] = parse.links.map((link) => ({
    link,
    width: link.value * scale,
    x0: nodes[link.source].rect.x + NODE_WIDTH,
    y0: 0,
    x1: nodes[link.target].rect.x,
    y1: 0,
  }));
  const indices = placed.map((_, i) => i);
  for (const node of nodes) {
    const outgoing = indices
      .filter((i) => parse.links[i].source === node.node.id)
      .sort(
        (a, b) =>
          nodes[parse.links[a].target].rect.y -
            nodes[parse.links[b].target].rect.y || a - b
      );
    let at = node.rect.y + (node.rect.height - sumWidths(placed, outgoing)) / 2;
    for (const i of outgoing) {
      placed[i].y0 = at + placed[i].width / 2;
      at += placed[i].width;
    }
    const incoming = indices
      .filter((i) => parse.links[i].target === node.node.id)
      .sort(
        (a, b) =>
          nodes[parse.links[a].source].rect.y -
            nodes[parse.links[b].source].rect.y || a - b
      );
    at = node.rect.y + (node.rect.height - sumWidths(placed, incoming)) / 2;
    for (const i of incoming) {
      placed[i].y1 = at + placed[i].width / 2;
      at += placed[i].width;
    }
  }
  return placed;
}

function sumWidths(
  placed: readonly PlacedSankeyLink[],
  ids: readonly number[]
): number {
  return ids.reduce((sum, i) => sum + placed[i].width, 0);
}

/* ------------------------------------------------------- text alternative */

export interface SankeyRow {
  source: string;
  target: string;
  value: number;
}

export function sankeyRows(parse: SankeyParse): SankeyRow[] {
  return parse.links.map((link) => ({
    source: parse.nodes[link.source].name,
    target: parse.nodes[link.target].name,
    value: link.value,
  }));
}

/** Nodes whose flow in and out differ — the places where flow appears or vanishes. */
export function unbalancedNodes(parse: SankeyParse): string[] {
  const { inflow, outflow } = flows(parse);
  return parse.nodes
    .filter((node) => {
      const gap = Math.abs(inflow[node.id] - outflow[node.id]);
      const sourceOrSink = inflow[node.id] === 0 || outflow[node.id] === 0;
      return (
        !sourceOrSink &&
        gap > 1e-9 * Math.max(inflow[node.id], outflow[node.id])
      );
    })
    .map((node) => node.name);
}

/** The flows as `Source -> Target: value`, the form the tool reads. */
export function sankeyText(parse: SankeyParse): string {
  return sankeyRows(parse)
    .map((row) => `${row.source} -> ${row.target}: ${row.value}`)
    .join("\n");
}
